import { Button, CircularProgress, IconButton, Typography } from '@mui/material'
import clsx from 'clsx'
import React, { ChangeEvent, useState } from 'react'
import {
    getIdsConsultationsConsultationId,
    getGetIdsConsultationsConsultationIdQueryKey,
    usePostIdsHealthcareservicesHcsIdConsultationsConsultationIdDocument,
    usePutIdsHealthcareservicesHcsIdConsultationsConsultationId,
} from '@toktokdoctech/tokapi-client'
import { useDropzone } from 'react-dropzone'
import CreateRoundedIcon from '@mui/icons-material/CreateRounded'
import useStyles from './_styles'
import useButtonStyles from '../../../styles/buttonsStyles'
import MediaViewer from '../../Common/MediaViewer'
import { isMobileOrTabletBrowser } from '../../../utils/commonUtils'
import TokTokDocDialog from '../../Common/Dialog'
import MediaRenamer from './MediaRenamer/MediaRenamer'
import { getSecureDocument, getSecureImage } from '../../../api/hds'
import PlannedConsultationEditorMediaListSortable from './PlannedConsultationEditorMediaListSortable'
import { displaySnackbar } from '../../../actions/main'
import { useAppDispatch } from '../../../store'
import { Media, PlannedConsultationEditorMediaListProps } from './_props'
import { attatchIdsToMedia } from './MediaRenamer/mediaRenamerUtils'
import { formatMediaFromRaw } from '../../../api/media'
import { tanstackQueryClient } from '../../../api/tanstackClient'
import useCurrentHcs from '../../../hooks/useCurrentHcs'

/**
 *
 * @documentation {useDropzone} https://react-dropzone.js.org/#section-basic-example
 */
const PlannedConsultationEditorMediaList = ({ initialMedias, id }: PlannedConsultationEditorMediaListProps) => {
    const classes = useStyles()
    const dispatch = useAppDispatch()
    const currentHcs = useCurrentHcs()
    const buttonClasses = useButtonStyles()
    const defaultMediaViewerState = {
        isOpen: false,
        type: undefined,
        media: undefined,
        localUrl: undefined,
    }

    const [mediaList, setMediaList] = useState<Media>(initialMedias)

    const [mediaViewerState, setMediaViewerState] = React.useState(defaultMediaViewerState)
    const [renamingTarget, setRenamingTarget] = React.useState(undefined)
    const [deletionTarget, setDeletionTarget] = React.useState(undefined)
    const [isLoadingMedia, setIsLoadingMedia] = React.useState(false)

    const addMediaMutation = usePostIdsHealthcareservicesHcsIdConsultationsConsultationIdDocument()
    const updateMediaMutation = usePutIdsHealthcareservicesHcsIdConsultationsConsultationId()

    // used to sync the media id with the backend after a media is added
    const syncMediaListWithBackend = async () => {
        const consultation = await getIdsConsultationsConsultationId(id)

        setMediaList({
            documents: consultation.documents || [],
            pictures: Array.isArray(consultation.pictures) ? consultation.pictures : [],
        })
    }

    const handleMediaListChange = async (data: Media) => {
        setIsLoadingMedia(true)
        try {
            const documents = attatchIdsToMedia(data.documents)
            const pictures = attatchIdsToMedia(data.pictures)

            const formattedDocs = documents.map((document: any) => formatMediaFromRaw(document))
            const formattedPics = pictures.map((picture: any) => formatMediaFromRaw(picture))

            const baseParams = {
                consultationId: id,
                hcsId: currentHcs.id,
            }

            const uploadedDocuments: Media[] = []
            for (const document of formattedDocs) {
                const response = await addMediaMutation.mutateAsync({
                    ...baseParams,
                    data: { File: document?.file },
                })

                uploadedDocuments.push({
                    ...document,
                    ...response,
                    url: response?.documentUrl,
                })
            }

            const uploadedPictures: Media[] = []
            for (const picture of formattedPics) {
                const response = await addMediaMutation.mutateAsync({
                    ...baseParams,
                    data: { File: picture.file },
                })

                uploadedPictures.push({
                    ...picture,
                    ...response,
                    url: response.documentUrl,
                })
            }

            setMediaList((prev) => ({
                documents: [...prev.documents, ...uploadedDocuments],
                pictures: [...prev.pictures, ...uploadedPictures],
            }))

            await syncMediaListWithBackend()
        } catch (error) {
            console.error(error)
            dispatch(
                displaySnackbar('error', "Une erreur est survenue lors de l'import des médias. Veuillez réessayer."),
            )
        }

        setIsLoadingMedia(false)
    }

    const handleUpdateMediaList = async ({ documents, pictures }: any) => {
        setIsLoadingMedia(true)
        try {
            const updatedMediaList = {
                documents: documents.map((document: any) => ({
                    ...document,
                })),
                pictures: pictures.map((picture: any) => ({
                    ...picture,
                })),
            }

            await updateMediaMutation.mutateAsync({
                consultationId: id,
                hcsId: currentHcs.id,
                data: {
                    initialAppointmentDate: null,
                    documents: updatedMediaList.documents,
                    pictures: updatedMediaList.pictures,
                },
            })

            const key = getGetIdsConsultationsConsultationIdQueryKey(id)
            tanstackQueryClient.invalidateQueries({ queryKey: key })
            await syncMediaListWithBackend()
        } catch (error) {
            console.error(error)
            dispatch(
                displaySnackbar(
                    'error',
                    'Une erreur est survenue lors de la mise à jour de la liste des médias. Veuillez réessayer.',
                ),
            )
        }
        setIsLoadingMedia(false)
    }

    const getReceivedMediasByType = (files: any, type: any) => {
        if (files && type) {
            return files.filter((file: any) => {
                const match = Array.isArray(type) ? type : [type]
                if (match.includes(file.type)) return file
                return undefined
            })
        }
        return undefined
    }

    const isFileSizeValid = (file: File) => file.size / 1024 / 1024 <= 5

    const handleOnChange = (rawFiles: File[] | FileList | null) => {
        const received = {
            documents: [],
            pictures: [],
        }

        if (rawFiles) {
            const files = Array.from(rawFiles).filter(isFileSizeValid)
            received.documents = getReceivedMediasByType(files, 'application/pdf') || []
            received.pictures = getReceivedMediasByType(files, ['image/jpeg', 'image/jpg', 'image/png']) || []
        }
        handleMediaListChange(received)
        if (fileInputRef && fileInputRef.current) fileInputRef.current.value = ''
    }

    const handleInputFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const hasInvalidFiles = Array.from(e.target.files || []).some((file) => !isFileSizeValid(file))

        if (hasInvalidFiles) {
            dispatch(
                displaySnackbar(
                    'error',
                    `Fichiers acceptés : Format JPG, PNG ou PDF, d'une taille maximale de 5 Mo. Vous pouvez importer jusqu'à 8 fichiers à la fois.`,
                    10000,
                ),
            )
            return
        }
        handleOnChange(e.target.files)
    }

    const { getRootProps, isDragAccept, isDragReject } = useDropzone({
        accept: {
            'image/jpg': [],
            'image/jpeg': [],
            'image/png': [],
            'application/pdf': [],
        },
        maxFiles: 8,
        onDrop: (files, rejections) => {
            const hasInvalidFiles = files.some((file) => !isFileSizeValid(file))
            if (rejections.length > 0 || hasInvalidFiles) {
                dispatch(
                    displaySnackbar(
                        'error',
                        `Fichiers acceptés : Format JPG, PNG ou PDF, d'une taille maximale de 5 Mo. Vous pouvez importer jusqu'à 8 fichiers à la fois.`,
                        10000,
                    ),
                )
                return
            }
            handleOnChange(files)
        },
    })

    const acceptStyle = {
        borderColor: '#00e676',
    }

    const rejectStyle = {
        borderColor: '#ff1744',
    }

    const style = React.useMemo(
        () => ({
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isDragAccept, isDragReject],
    )

    const getExistingMediaNames = () => {
        const result = {}

        Object.keys(mediaList).forEach((key) => {
            // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            const collection = mediaList[key].map((media: any) => media.name)
            // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            result[key] = collection
        })
        return result
    }

    const getListLabel = (type: any) => {
        if (type === 'documents') return 'Documents'
        if (type === 'pictures') return 'Photos'
        return undefined
    }

    const getNoContentLabel = (type: any) => {
        if (type === 'documents') return 'Aucun documents'
        if (type === 'pictures') return 'Aucune photos'
        return undefined
    }

    const fileInputRef = React.createRef<HTMLInputElement>()

    const handleAddButtonClick = () => {
        if (fileInputRef && fileInputRef.current) fileInputRef.current.click()
    }

    const downloadPicture = async (target: any) => {
        if (target.localUrl) return target.localUrl

        const image = await getSecureImage(target.url)
        setMediaList({
            pictures: mediaList.pictures.map((picture) => {
                if (picture.id === target.id) {
                    // @ts-expect-error TS(2339) FIXME: Property 'localUrl' does not exist on type 'never'... Remove this comment to see the full error message
                    picture.localUrl = image.localUrl
                }
                return picture
            }),
            documents: mediaList.documents,
        })
        // @ts-expect-error TS(2339) FIXME: Property 'localUrl' does not exist on type 'void |... Remove this comment to see the full error message
        return image.localUrl
    }

    const hasNext = () => {
        // @ts-expect-error TS(2339) FIXME: Property 'index' does not exist on type 'undefined... Remove this comment to see the full error message
        const { index } = mediaViewerState.media
        return mediaList.pictures[index + 1]
    }
    const hasPrevious = () => {
        // @ts-expect-error TS(2339) FIXME: Property 'index' does not exist on type 'undefined... Remove this comment to see the full error message
        const { index } = mediaViewerState.media
        return mediaList.pictures[index - 1]
    }
    const handleNext = async () => {
        // @ts-expect-error TS(2339) FIXME: Property 'index' does not exist on type 'undefined... Remove this comment to see the full error message
        const { index } = mediaViewerState.media
        const target = mediaList.pictures[index + 1]
        const localUrl = await downloadPicture(target)

        setMediaViewerState({
            ...mediaViewerState,
            media: target,
            localUrl,
        })
    }
    const handlePrevious = async () => {
        // @ts-expect-error TS(2339) FIXME: Property 'index' does not exist on type 'undefined... Remove this comment to see the full error message
        const { index } = mediaViewerState.media
        const target = mediaList.pictures[index - 1]
        const localUrl = await downloadPicture(target)

        setMediaViewerState({
            ...mediaViewerState,
            media: target,
            localUrl,
        })
    }

    const handleMediaPreview = async (media: any, type: any) => {
        setIsLoadingMedia(true)
        if (type === 'picture') {
            const localUrl = await downloadPicture(media)
            setIsLoadingMedia(false)
            setMediaViewerState({
                isOpen: true,
                type,
                media,
                localUrl,
            })
        }
        if (type === 'document') {
            try {
                const doc = await getSecureDocument(media.url)
                setIsLoadingMedia(false)
                // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
                window.open(doc, '_blank')
            } catch (error) {
                console.log(error)
            }
        }
    }

    const handleMediaRenamerSave = ({ id, newName }: any) => {
        Object.keys(mediaList).forEach((key) => {
            // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            mediaList[key] = mediaList[key].map((media: any) => {
                if (media.id === id) {
                    media.name = newName
                }
                return media
            })
        })
        handleUpdateMediaList(mediaList)
        setRenamingTarget(undefined)
    }

    const handleDeleteMedia = (mediaId: any) => {
        Object.keys(mediaList).forEach((key) => {
            // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            mediaList[key] = mediaList[key].filter((media: any) => media.id !== mediaId)
        })
        handleUpdateMediaList(mediaList)
        setDeletionTarget(undefined)
    }

    const handleMediaReorder = (reordoredMedias: any, type: any) => {
        const updatedIndexes = reordoredMedias.map((media: any, index: any) => {
            media.index = index
            return media
        })

        const updatedMedias = {
            ...mediaList,
            [type]: updatedIndexes,
        }
        setMediaList(updatedMedias)
        handleUpdateMediaList(updatedMedias)
    }

    const toolbarEditButton = isMobileOrTabletBrowser() ? (
        <IconButton
            onClick={() => setRenamingTarget(mediaViewerState.media)}
            size="large"
            className={classes.PlannedConsultationEditorMediaViewerEditionButton}
        >
            <CreateRoundedIcon />
        </IconButton>
    ) : (
        <Button
            className={classes.PlannedConsultationEditorMediaViewerEditionButton}
            onClick={() => setRenamingTarget(mediaViewerState.media)}
        >
            <CreateRoundedIcon />
            <span>Editer</span>
        </Button>
    )
    const mediaViewerToolbarButtons = [toolbarEditButton]

    const mediaCollections = Object.keys(mediaList)
        .map((key) => {
            // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            const collection = mediaList[key]

            return {
                type: key,
                collection: collection.length > 0 ? collection : undefined,
            }
        })
        .filter((collection) => collection.collection !== undefined)

    const renderColectionsLists = () =>
        Object.keys(mediaList).map((key) => {
            // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            const content = mediaList[key]
            const listLabel = getListLabel(key)

            return (
                <div key={key} className={classes.PlannedConsultationEditorMediaListContentContainer}>
                    {listLabel && (
                        <Typography className={classes.PlannedConsultationEditorMediaListContentContainerLabel}>
                            {listLabel}
                        </Typography>
                    )}
                    <div className={classes.PlannedConsultationEditorMediaListContentContainerList}>
                        {content.length ? (
                            <PlannedConsultationEditorMediaListSortable
                                medias={content}
                                onPreview={handleMediaPreview}
                                onRenameRequest={setRenamingTarget}
                                onDeleteRequest={setDeletionTarget}
                                onReorder={(reordored: any) => handleMediaReorder(reordored, key)}
                            />
                        ) : (
                            <Typography>{getNoContentLabel(key)}</Typography>
                        )}
                    </div>
                </div>
            )
        })

    const renderEmptyHint = () => (
        <div className={classes.PlannedConsultationEditorMediaListHintContainer}>
            <Typography>
                Fichiers acceptés : Format JPG, PNG ou PDF, d'une taille maximale de 5 Mo. Vous pouvez importer jusqu'à
                8 fichiers à la fois.
            </Typography>
        </div>
    )

    return (
        <>
            <div
                className={clsx(classes.PlannedConsultationEditorMediaList, classes.PlannedConsultationEditorContainer)}
            >
                {isLoadingMedia && (
                    <div className={classes.PlannedConsultationEditorMediaListLoader}>
                        <CircularProgress />
                    </div>
                )}
                <div className={classes.PlannedConsultationEditorContainerHeader}>
                    <div className={classes.PlannedConsultationEditorContainerHeaderTitle}>
                        <Typography className={classes.PlannedConsultationEditorContainerTitle}>
                            Pièces jointes
                        </Typography>
                        <Typography className={classes.PlannedConsultationEditorContainerSubtitle}>
                            Glissez / déposez vos fichiers dans la zone ci dessous ou cliquez sur le bouton "Ajouter"
                        </Typography>
                    </div>
                    <div className={classes.PlannedConsultationEditorContainerHeaderButtonContainer}>
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={handleAddButtonClick}
                            className={buttonClasses.ButtonBlack}
                        >
                            Ajouter
                        </Button>
                        <input
                            style={{
                                display: 'none',
                            }}
                            multiple
                            type="file"
                            onInput={handleInputFileChange}
                            id="PlannedConsultationEditorMediaList-uploadButton"
                            ref={fileInputRef}
                            accept="application/pdf, .jpeg, .jpg, .png"
                        />
                    </div>
                </div>
                <Button
                    className={clsx('container', classes.PlannedConsultationEditorMediaListDropzoneContainer)}
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                    }}
                    onKeyUp={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                    }}
                >
                    <Button
                        {...getRootProps({
                            style,
                            className: clsx('dropzone', classes.PlannedConsultationEditorMediaListDropzone),
                        })}
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                        }}
                        onKeyUp={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                        }}
                    >
                        <div className={classes.PlannedConsultationEditorMediaListContent}>
                            <div className={classes.PlannedConsultationEditorMediaListContentContainers}>
                                {mediaCollections.length > 0 ? renderColectionsLists() : renderEmptyHint()}
                            </div>
                        </div>
                    </Button>
                </Button>
            </div>
            {mediaViewerState.isOpen && mediaViewerState.type === 'picture' && mediaViewerState.media && (
                <MediaViewer
                    source={mediaViewerState.localUrl}
                    hasNext={hasNext()}
                    hasPrevious={hasPrevious()}
                    handleNext={handleNext}
                    handlePrevious={handlePrevious}
                    // @ts-expect-error TS(2339) FIXME: Property 'name' does not exist on type 'never'.
                    mediaName={mediaViewerState.media.name}
                    toolbarButtons={mediaViewerToolbarButtons}
                    onClose={() => {
                        setMediaViewerState(defaultMediaViewerState)
                    }}
                />
            )}
            {renamingTarget && (
                <MediaRenamer
                    open
                    // @ts-expect-error TS(2322) FIXME: Type '() => void' is not assignable to type '() =>... Remove this comment to see the full error message
                    handleClose={() => setRenamingTarget(undefined)}
                    mediaTarget={renamingTarget}
                    // @ts-expect-error TS(2322) FIXME: Type '({ id, newName }: any) => void' is not assig... Remove this comment to see the full error message
                    onSave={handleMediaRenamerSave}
                    // @ts-expect-error TS(2740) FIXME: Type '{}' is missing the following properties from... Remove this comment to see the full error message
                    existingNames={getExistingMediaNames()}
                />
            )}
            {deletionTarget && (
                <TokTokDocDialog
                    isOpen={!!deletionTarget}
                    handleClose={() => setDeletionTarget(undefined)}
                    // @ts-expect-error TS(2339) FIXME: Property 'id' does not exist on type 'never'.
                    handleSave={() => handleDeleteMedia(deletionTarget.id)}
                    saveTitle="Supprimer"
                    title="Suppression"
                    isDeleteAction
                    disableEscape
                >
                    {/* @ts-expect-error TS(2339) FIXME: Property 'name' does not exist on type 'never'. */}
                    <Typography variant="h4">{`Supprimer "${deletionTarget.name}" ?`}</Typography>
                </TokTokDocDialog>
            )}
        </>
    )
}

export default PlannedConsultationEditorMediaList
